import { ErrorInfo } from 'react';

// 서버와 합의한 에러 코드\
export const ERROR_CODE_CONSTANTS = [
  // graphql built in error codes
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'INTERNAL_ERROR',
  'AUTH_REQUIRED',
  'PERMISSION_DENIED',
  'INVALID_INPUT',
  'MISSING_PARAMETER',
  'NETWORK_ERROR',
  'SERVER_INTERNAL',
  'API_RATE_LIMIT',
  'UNKNOWN_ERROR',
  // custom error codes
  'POI_NOT_FOUND',
  'SURVEY_NOT_FOUND',
  'SURVEY_DELETED',
  'SURVEY_INVITE_NOT_FOUND',
  'INTERNAL_SERVER_ERROR',
  'USER_NOT_AUTHENTICATED',
  'INVALID_INVITE_TOKEN',
  'INVALID_TOGGLE_POI_REQUEST',
  'SURVEY_SAVE_FAILED',
  'INVALID_OWNER_ROLE_REQUEST',
  'SURVEY_USER_NOT_FOUND',
  'OWNER_ROLE_REQUIRED',
  'EXIST_SURVEY_USER',
  'ACCESS_DENIED',
  'REVIEW_ACCESS_DENIED',
  'SURVEY_ACCESS_DENIED',
  'TRAVEL_REVIEW_NOT_FOUND',
  'SURVEY_USER_LIST_ACCESS_DENIED',
] as const;

export type ErrorCode = (typeof ERROR_CODE_CONSTANTS)[number];

/**
 * 클라이언트 컴포넌트에서 useActionHandler 훅을 사용해 서버 액션을 호출할 때 주로 활용합니다.
 * - ALERT: alert-modal 사용
 * - CUSTOM: 훅을 사용하는 컴포넌트에서 직접 처리 로직 구현
 * - ERROR_BOUNDARY: Error Boundary를 통해 처리
 * - AUTH: 인증 관련 에러
 */
export type ErrorCategory = 'ALERT' | 'CUSTOM' | 'ERROR_BOUNDARY' | 'AUTH';

export const CODE_CATEGORIES: Record<ErrorCategory, Set<ErrorCode>> = {
  ALERT: new Set([
    'BAD_REQUEST',
    'FORBIDDEN',
    'INTERNAL_ERROR',
    'AUTH_REQUIRED',
    'PERMISSION_DENIED',
    'INVALID_INPUT',
    'MISSING_PARAMETER',
    'SERVER_INTERNAL',
    'API_RATE_LIMIT',
    'POI_NOT_FOUND',
    'SURVEY_NOT_FOUND',
    'SURVEY_INVITE_NOT_FOUND',
    'INTERNAL_SERVER_ERROR',
    'INVALID_INVITE_TOKEN',
    'INVALID_TOGGLE_POI_REQUEST',
    'SURVEY_SAVE_FAILED',
    'INVALID_OWNER_ROLE_REQUEST',
    'SURVEY_USER_NOT_FOUND',
    'OWNER_ROLE_REQUIRED',
    'EXIST_SURVEY_USER',
    'ACCESS_DENIED',
    'REVIEW_ACCESS_DENIED',
    'TRAVEL_REVIEW_NOT_FOUND',
    'SURVEY_USER_LIST_ACCESS_DENIED',
    'UNKNOWN_ERROR',
  ]),
  CUSTOM: new Set([]),
  ERROR_BOUNDARY: new Set(['NETWORK_ERROR', 'NOT_FOUND']),
  AUTH: new Set(['UNAUTHORIZED', 'USER_NOT_AUTHENTICATED', 'SURVEY_ACCESS_DENIED']),
};

export const ERROR_MESSAGES: { [key in ErrorCode]?: string } = {
  BAD_REQUEST: '잘못된 요청입니다.',
  UNAUTHORIZED: '로그인이 필요합니다.',
  FORBIDDEN: '접근 권한이 없습니다.',
  NOT_FOUND: '요청한 정보를 찾을 수 없습니다.',
  INTERNAL_ERROR: '서버 처리 중 오류가 발생했습니다.',
  NETWORK_ERROR: '네트워크 연결을 확인해주세요.',
  UNKNOWN_ERROR: '알 수 없는 오류가 발생했습니다.',
  POI_NOT_FOUND: '장소 정보를 찾을 수 없습니다.',
  SURVEY_NOT_FOUND: '일정표 정보를 찾을 수 없습니다.',
  SURVEY_DELETED: '일정표가 삭제되었습니다.',
  SURVEY_INVITE_NOT_FOUND: '일정표 초대 정보를 찾을 수 없습니다.',
  INTERNAL_SERVER_ERROR: '서버 처리 중 오류가 발생했습니다.',
  INVALID_INVITE_TOKEN: '초대 토큰이 유효하지 않습니다.',
  INVALID_TOGGLE_POI_REQUEST: '찜카 플래너 정보를 토글할 수 없습니다.',
  SURVEY_SAVE_FAILED: '일정표 저장에 실패했습니다.',
};

export interface ApiActionError {
  code: ErrorCode;
  message: string;
  category: ErrorCategory;
  originalError?: unknown;
}

/**
 * 주어진 에러가 ApiActionError 타입인지 확인하는 타입 가드
 */
export function isApiActionError(error: unknown): error is ApiActionError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'category' in error &&
    typeof error.code === 'string' &&
    typeof error.message === 'string' &&
    typeof error.category === 'string' &&
    ERROR_CODE_CONSTANTS.includes(error.code as ErrorCode)
  );
}

export function normalizeError(error: unknown): ApiActionError {
  if (isApiActionError(error)) {
    return error;
  }

  let determinedCode: ErrorCode = 'UNKNOWN_ERROR';
  let message: string | undefined;

  if (error instanceof Error) {
    message = error.message;
    if (error.message.toLowerCase().includes('network') || error.message.toLowerCase().includes('failed to fetch')) {
      determinedCode = 'NETWORK_ERROR';
    }
  } else {
    try {
      message = JSON.stringify(error);
    } catch {
      message = '알 수 없는 오류가 발생했습니다.';
    }
  }

  let determinedCategory: ErrorCategory = 'ALERT';
  for (const cat in CODE_CATEGORIES) {
    if (CODE_CATEGORIES[cat as ErrorCategory].has(determinedCode)) {
      determinedCategory = cat as ErrorCategory;
      break;
    }
  }

  const finalMessage = message || ERROR_MESSAGES[determinedCode] || '알 수 없는 오류가 발생했습니다.';

  return {
    code: determinedCode,
    message: finalMessage,
    category: determinedCategory,
    originalError: error,
  };
}

export const parseNextJsErrorStack = (error: Error) => {
  const stackLines = error.stack?.toString().split('\n') || [];
  const stackInfo = [];
  // Next.js 번들 파일 패턴 (해시 포함)
  const regex = /at\s+(?:.*\s+\()?(?:(.+?):(\d+):(\d+))/;

  for (const line of stackLines) {
    const match = line.match(regex);
    if (match) {
      stackInfo.push({
        file: match[1].split('/').pop(),
        line: parseInt(match[2], 10),
        column: parseInt(match[3], 10),
        fullTrace: line.trim(),
      });
    }
  }
  return stackInfo;
};

// 첫 번째 에러 위치만 가져오기
export const getFirstErrorLocation = (error: Error) => {
  const locations = parseNextJsErrorStack(error);
  return locations.length > 0 ? locations[0] : null;
};

export const reportError = (error: Error, info?: ErrorInfo) => {
  const isDeletedSurvey = error.message.includes('SURVEY_DELETED');
  if (isDeletedSurvey) return;

  const stack = parseNextJsErrorStack(error);
  if (stack.length > 0) {
    fetch('/api/error-handler', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stack, info }),
    }).catch((fetchError) => {
      console.error('Failed to report error to /api/error-handler:', fetchError);
    });
  } else {
    console.warn('Could not parse error location from stack trace for reporting.');
  }
};

export const getErrorMessage = (errorCode: ErrorCode): string => {
  return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.UNKNOWN_ERROR || '알 수 없는 오류가 발생했습니다.';
};

export const parseErrorObject = (error: any): { code: ErrorCode; message: string } => {
  let errorObject = { code: 'UNKNOWN_ERROR' as ErrorCode, message: '' };

  try {
    if (typeof error.message === 'string' && error.message.trim().startsWith('{')) {
      try {
        errorObject = JSON.parse(error.message);
      } catch (parseError) {
        errorObject.message = error.message;
      }
    } else {
      errorObject.message = error.message || error.toString();
    }
  } catch (fallbackError) {
    errorObject.message = '에러가 발생했습니다.';
  }

  return errorObject;
};
